import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30s — avoids refetch on tab switch / remount
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Query key factories for consistent keys + easy invalidation.
// All user-specific keys are scoped to userId so React Query never serves
// another user's cached data after a session switch. A queryClient.clear()
// on SIGNED_OUT (see auth.tsx) is defense-in-depth for everything else.
export const queryKeys = {
  userSkills: (userId: string) => ["userSkills", userId] as const,
  stories: (userId: string) => ["stories", userId] as const,
  experiences: (userId: string) => ["experiences", userId] as const,
  philosophies: (userId: string) => ["philosophies", userId] as const,
  applications: (userId: string) => ["applications", userId] as const,
  pendingMiningSkills: (userId: string) => ["pendingMiningSkills", userId] as const,
  swipeDeck: (userId: string) => ["swipeDeck", userId] as const,
  companies: ["companies"] as const,
  savedJobIds: (userId: string) => ["savedJobIds", userId] as const,
  priorityJobIds: (userId: string) => ["priorityJobIds", userId] as const,
  companyJobs: (companyId: string) => ["companyJobs", companyId] as const,
  unifiedJobSearch: (query: string, remote: string, order: string, limit: number, offset: number) =>
    ["unifiedJobSearch", query, remote, order, limit, offset] as const,
  unifiedJobSearchRanked: (
    userId: string,
    query: string,
    remote: string,
    sortMode: string,
    limit: number,
    offset: number,
  ) => ["unifiedJobSearchRanked", userId, query, remote, sortMode, limit, offset] as const,
  unifiedJobSkills: (jobIds: readonly string[]) => ["unifiedJobSkills", ...jobIds] as const,
  gamification: (userId: string) => ["gamification", userId] as const,
  profileStats: (userId: string) => ["profileStats", userId] as const,
  companyDiscovery: ["companyDiscovery"] as const,
  miningSessions: (userId: string) => ["miningSessions", userId] as const,
  lenses: (userId: string) => ["lenses", userId] as const,
  lensRefSets: (userId: string) => ["lensRefSets", userId] as const,
  vaultProposals: (userId: string) => ["vaultProposals", userId] as const,
  vaultProposalCount: (userId: string) => ["vaultProposalCount", userId] as const,
  vaultSyncInfo: (userId: string) => ["vaultSyncInfo", userId] as const,
  vaultHistory: (userId: string) => ["vaultHistory", userId] as const,
  vaultCommitDiff: (userId: string, sha: string) => ["vaultCommitDiff", userId, sha] as const,
  miningDocuments: (userId: string) => ["miningDocuments", userId] as const,
  preferenceModel: (userId: string) => ["preferenceModel", userId] as const,
  learningPaths: (userId: string) => ["learningPaths", userId] as const,
};
